import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Btn, Modal, Input, Select, Badge, Textarea, NumInput } from "@/components/UI";
import { Plus, FlaskConical, CheckCircle, XCircle, ArrowRightCircle, History, Download, Copy, Trash2, Edit3, FileText, Sparkles, Send, RotateCcw, AlertTriangle, Wand2, ShieldCheck, Tag, Play, Package, Truck, Clock, GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import { useDragReorder } from "@/lib/useDragReorder";
import { FilterAndReportBar } from "@/components/FilterAndReportBar";
import MeetingsBanner from "@/components/MeetingsBanner";
import { openPdfPreview } from "@/lib/pdfPreview";
import { RegulatoryCheckModal } from "@/components/RegulatoryCheckModal";
import SmartSuggestInput from "@/components/SmartSuggestInput";
import { usePagination, ListPagination } from "@/components/ListPagination";
import { diffRevisionLines, diffKindClass } from "@/lib/revisionDiff";

// -------- Sample & approval badges --------
const SAMPLE_BADGE = {
  processing: { label: "İşlemde", cls: "bg-orange-100 text-orange-700 ring-orange-600/20" },
  ready:      { label: "Numune Hazır", cls: "bg-yellow-100 text-yellow-800 ring-yellow-600/20" },
  delivered:  { label: "Teslim Edildi", cls: "bg-emerald-100 text-emerald-700 ring-emerald-600/20" },
};
const APPROVAL_BADGE = {
  pending:  { label: "Beklemede",     cls: "bg-orange-100 text-orange-700 ring-orange-600/20" },
  approved: { label: "Onaylandı",     cls: "bg-emerald-100 text-emerald-700 ring-emerald-600/20" },
  rejected: { label: "Onaylanmadı",   cls: "bg-red-100 text-red-700 ring-red-600/20" },
};
const BadgePill = ({ level, children, testid }) => {
  const b = level || { label: "—", cls: "bg-slate-100 text-slate-600 ring-slate-500/20" };
  return <span data-testid={testid} className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${b.cls}`}>{children ?? b.label}</span>;
};

// -------- Autocomplete material input --------
function MaterialAutocomplete({ value, materials, onChangeText, onSelectExisting, testid }) {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const suggestions = useMemo(() => {
    if (!q || q.length < 1) return [];
    const t = q.toLowerCase();
    const starts = [], contains = [];
    for (const m of materials) {
      const n = (m.name || "").toLowerCase();
      if (n.startsWith(t)) starts.push(m);
      else if (n.includes(t)) contains.push(m);
    }
    return [...starts, ...contains].slice(0, 8);
  }, [q, materials]);
  return (
    <div ref={wrapRef} className="relative">
      <input
        value={q}
        onChange={(e) => {
          const v = e.target.value;
          setQ(v);
          setOpen(true);
          // live-sync name to parent so blur can't clobber later
          onChangeText(v);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setOpen(false); } }}
        placeholder="Malzeme adı yazın… (envanterden öneri gelir)"
        className="w-full px-2 py-1 text-xs border rounded"
        data-testid={testid}
      />
      {open && q && (
        <div className="absolute z-30 mt-1 left-0 right-0 max-h-56 overflow-auto bg-white border border-slate-200 rounded-md shadow-lg text-xs">
          {suggestions.length > 0 ? suggestions.map((m) => (
            <button
              key={m.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onSelectExisting(m); setOpen(false); }}
              className="w-full text-left px-2 py-1.5 hover:bg-indigo-50 flex justify-between gap-2 border-b border-slate-100 last:border-0"
            >
              <span className="truncate">{m.name}</span>
              <span className="text-[10px] text-slate-500 shrink-0">{m.unit_cost} {m.currency}/{m.unit}</span>
            </button>
          )) : (
            <div className="px-2 py-1.5 text-slate-500 italic">Envanterde kayıt bulunamadı — yazdığınız değer manuel Ar-Ge malzemesi olarak kullanılacak.</div>
          )}
        </div>
      )}
    </div>
  );
}

// -------- Detail viewer --------
function DetailView({ project, customers, onClose, onPdf, onLabel }) {
  if (!project) return null;
  const cust = customers.find(c => c.id === project.customer_id);
  const c = project.cost || {};
  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2">
        {onLabel && <Btn size="sm" variant="secondary" onClick={onLabel} data-testid="rd-detail-label" title="Etiket Yazdır (4-lü 50×30 mm)"><Tag size={14}/></Btn>}
        <Btn size="sm" variant="secondary" onClick={onPdf} data-testid="rd-detail-pdf" title="R&D Ürün PDF"><FileText size={14}/></Btn>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div><span className="text-slate-500 text-xs uppercase">Proje No</span><div className="font-mono">{project.project_no}</div></div>
        <div><span className="text-slate-500 text-xs uppercase">Revizyon</span><div className="font-mono">{project.revision}</div></div>
        <div><span className="text-slate-500 text-xs uppercase">Ürün Adı</span><div>{project.product_name || project.name}</div></div>
        <div><span className="text-slate-500 text-xs uppercase">Müşteri</span><div>{cust?.legal_name || "—"}</div></div>
        <div><span className="text-slate-500 text-xs uppercase">Ürün Miktarı</span><div>{project.unit_ml} ml</div></div>
        <div><span className="text-slate-500 text-xs uppercase">EUR Kuru</span><div>{(project.eur_try_rate_snapshot || 0).toFixed(4)}</div></div>
        <div><span className="text-slate-500 text-xs uppercase">Numune</span><div><BadgePill level={SAMPLE_BADGE[project.sample_status]}/>{project.sample_delivery_date ? ` — ${project.sample_delivery_date.slice(0,10)}`:""}</div></div>
        <div><span className="text-slate-500 text-xs uppercase">Onay</span><div><BadgePill level={APPROVAL_BADGE[project.status]}/></div></div>
        <div><span className="text-slate-500 text-xs uppercase">Maliyet / kg</span><div className="font-mono text-indigo-700">€{(c.cost_per_kg_eur||0).toFixed(4)}</div></div>
        <div><span className="text-slate-500 text-xs uppercase">Birim Maliyet</span><div className="font-mono text-emerald-700">€{(c.unit_cost_eur||0).toFixed(4)}</div></div>
      </div>
      <div className="border-t border-slate-200 pt-2">
        <div className="text-sm font-semibold mb-2">Bileşenler</div>
        <table className="w-full text-xs border">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left px-2 py-1">Malzeme</th>
              <th className="text-left px-2 py-1">Kaynak</th>
              <th className="text-left px-2 py-1">Birim</th>
              <th className="text-right px-2 py-1">% / Adet</th>
              <th className="text-right px-2 py-1">Birim €</th>
              <th className="text-left px-2 py-1">Notlar</th>
            </tr>
          </thead>
          <tbody>
            {(project.lines || []).map((l, i) => (
              <tr key={i} className="border-t">
                <td className="px-2 py-1">{l.material_name}</td>
                <td className="px-2 py-1">{l.is_manual ? <span className="text-amber-700">Manuel</span> : <span className="text-indigo-700">Envanter</span>}</td>
                <td className="px-2 py-1">{l.unit}</td>
                <td className="px-2 py-1 text-right font-mono">{l.unit === "kg" ? `%${(l.percentage||0).toFixed(2)}` : `${l.qty_adet||0}`}</td>
                <td className="px-2 py-1 text-right font-mono">€{(l.unit_cost_eur||0).toFixed(4)}</td>
                <td className="px-2 py-1 text-slate-500">{l.notes||"—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="text-xs mt-2 text-slate-600">Toplam Formül Yüzdesi: <span className={`font-mono ${Math.abs((c.total_pct||0)-100)<0.01?"text-emerald-700":"text-amber-600"}`}>%{(c.total_pct||0).toFixed(2)}</span></div>
      </div>
      {project.notes && <div className="border-t border-slate-200 pt-2 text-xs"><span className="font-semibold">Notlar:</span> {project.notes}</div>}
      <div className="flex justify-end pt-2"><Btn variant="secondary" onClick={onClose}>Kapat</Btn></div>
    </div>
  );
}

// -------- Main page --------
export function RDPortalPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "system_admin";
  const [rows, setRows] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [revsOpen, setRevsOpen] = useState(null);
  const [revs, setRevs] = useState([]);
  const [detailOpen, setDetailOpen] = useState(null);
  const [transferOpen, setTransferOpen] = useState(null);
  const [tform, setTform] = useState({ customer_id: "", brand_id: "", product_id: "", version: "v1", useNewProduct: false, new_product: { name: "", fill_qty: 100, fill_unit: "ml", units_per_carton: 24, shelf_life_days: 730 }, confirm_manual: false });
  const [transferWarn, setTransferWarn] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [sampleModal, setSampleModal] = useState(null);
  const [sampleForm, setSampleForm] = useState({ sample_status: "processing", sample_delivery_date: "" });
  const [filteredRows, setFilteredRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState({});
  const [aiOpen, setAiOpen] = useState(false);       // iter122 — Formül üretici (üst buton)
  const [aiContentOpen, setAiContentOpen] = useState(null); // iter162 — Ürün İçerik Asistanı (liste satırı)
  const [complianceFor, setComplianceFor] = useState(null); // iter188 — Mevzuata Göre Kontrol Et (shared modal)
  const [labelPreview, setLabelPreview] = useState(null);  // iter260 — R&D etiket modal önizleme {url, id}

  const empty = { product_name: "", customer_id: "", customer_name: "", unit_ml: 100, working_quantity_kg: null, working_unit: "g", lines: [], notes: "", sample_status: "processing", sample_delivery_date: "", revision_note: "" };
  const [f, setF] = useState(empty);

  const load = useCallback(() => Promise.all([api.get("/rd-samples"), api.get("/materials"), api.get("/customers"), api.get("/products"), api.get("/brands")])
    .then(([s, m, c, p, b]) => { setRows(s); setMaterials(m); setCustomers(c); setProducts(p); setBrands(b); }), []);
  useEffect(() => { load(); }, [load]);

  // -------- Live cost computation (mirrors backend) --------
  const eurRate = useMemo(() => 35.0, []); // display fallback; backend uses live rate
  const linesComputed = useMemo(() => {
    return (f.lines || []).map(l => {
      let unit_cost_eur = 0;
      if (l.is_manual) {
        const cost = parseFloat(l.manual_cost || 0);
        const cur = (l.manual_currency || "EUR").toUpperCase();
        unit_cost_eur = cur === "EUR" ? cost : (cur === "TRY" ? cost / eurRate : cost);
      } else if (l.material_id) {
        const m = materials.find(x => x.id === l.material_id);
        if (m) {
          const cur = (m.currency || "TRY").toUpperCase();
          const cost = parseFloat(m.unit_cost || 0);
          unit_cost_eur = cur === "EUR" ? cost : (cur === "TRY" ? cost / eurRate : cost);
        }
      }
      return { ...l, unit_cost_eur };
    });
  }, [f.lines, materials, eurRate]);

  const totalPct = linesComputed.filter(l => (l.unit || "kg") === "kg").reduce((s, l) => s + (parseFloat(l.percentage) || 0), 0);
  const costPerKg = linesComputed.filter(l => (l.unit || "kg") === "kg").reduce((s, l) => s + ((parseFloat(l.percentage) || 0) / 100) * (l.unit_cost_eur || 0), 0);
  const adetTotal = linesComputed.filter(l => (l.unit || "kg") === "adet").reduce((s, l) => s + ((parseFloat(l.qty_adet) || 0) * (l.unit_cost_eur || 0)), 0);
  const massKg = (parseFloat(f.unit_ml) || 0) / 1000;
  const unitCostEur = costPerKg * massKg + adetTotal;

  // -------- Line handlers --------
  const addLine = () => setF(prev => ({ ...prev, lines: [...(prev.lines || []), { material_id: "", material_name: "", is_manual: false, unit: "kg", percentage: 0, qty_adet: 0, manual_cost: 0, manual_currency: "EUR", notes: "" }] }));
  const updLine = (i, patch) => setF(prev => ({ ...prev, lines: prev.lines.map((l, j) => j === i ? { ...l, ...patch } : l) }));
  // iter297 — R&D hammadde satırları drag-and-drop yeniden sıralama.
  const linesDnd = useDragReorder(() => f.lines || [], (arr) => setF(prev => ({ ...prev, lines: arr })));
  const rmLine = (i) => setF(prev => ({ ...prev, lines: prev.lines.filter((_, j) => j !== i) }));

  const pickExisting = (i, mat) => {
    setF(prev => {
      const dup = prev.lines.some((l, j) => j !== i && l.material_id === mat.id);
      if (dup && !window.confirm(`${mat.name} bu projede zaten bulunmaktadır. Yine de eklemek istiyor musunuz?`)) return prev;
      return {
        ...prev,
        lines: prev.lines.map((l, j) => j === i ? {
          ...l,
          material_id: mat.id,
          material_name: mat.name,
          is_manual: false,
          unit: (mat.unit || "kg").toLowerCase() === "adet" || (mat.unit || "").toLowerCase() === "pcs" ? "adet" : "kg",
          manual_cost: 0,
          manual_currency: "EUR",
        } : l),
      };
    });
  };
  // Live text change from autocomplete: sync name and mark manual if no exact inventory match.
  const changeLineText = (i, name) => {
    const exact = materials.find(m => (m.name || "").toLowerCase() === (name || "").toLowerCase());
    if (exact) {
      updLine(i, { material_id: exact.id, material_name: exact.name, is_manual: false });
    } else {
      updLine(i, { material_id: null, material_name: name, is_manual: !!name });
    }
  };

  // -------- Save --------
  const save = async () => {
    if (!f.product_name || !f.product_name.trim()) { alert("Ürün adı zorunludur"); return; }
    if (!f.customer_id) { alert("Müşteri seçin"); return; }
    if (!f.lines || f.lines.length === 0) { alert("En az bir bileşen ekleyin"); return; }
    // basic client-side percent check for UX (backend re-validates)
    if (Math.abs(totalPct - 100) > 0.01) {
      alert(`Formül toplam yüzdesi %100 olmalıdır. Mevcut: %${totalPct.toFixed(2)}`);
      return;
    }
    try {
      if (editing) await api.put(`/rd-samples/${editing.id}`, f);
      else         await api.post("/rd-samples", f);
      setOpen(false); setEditing(null); setF(empty);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };

  // -------- Actions --------
  const decide = async (id, approved, reason) => {
    try {
      await api.post(`/rd-samples/${id}/decide`, { approved, notes: reason || null });
      setRejectOpen(null); setRejectReason("");
      load();
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const del = async (id) => { if (window.confirm("Bu proje arşivlensin mi?")) { try { await api.del(`/rd-samples/${id}`); load(); } catch (e) { alert(e.response?.data?.detail || "Hata"); } } };
  const copy = async (id) => {
    // iter257: real UI reproduction of "Formül Kopyala" defect — user
    // reported an alert even though the backend returns 200. Root cause
    // suspected in the load() refresh throwing on a legacy field. We now
    // guard the copy chain independently: (1) request, (2) refresh, and
    // surface a precise message per step so silent failures are eliminated.
    try {
      const r = await api.post(`/rd-samples/${id}/copy`, {});
      try {
        await load();
      } catch (loadErr) {
        // Copy succeeded server-side, refresh failed client-side.
        // Do not roll back — the new record exists and will appear on
        // next navigation/refresh.
        console.error("[rd copy] refresh after copy failed", loadErr);
        alert(`Kopya oluşturuldu (${r?.project_no || r?.code || "yeni proje"}). Liste güncellenemedi — sayfayı yenileyin.`);
        return;
      }
    } catch (e) {
      alert(e.response?.data?.detail || e.message || "Kopya başarısız");
    }
  };
  const showRevs = async (r) => { const rs = await api.get(`/rd-samples/${r.id}/revisions`); setRevs(rs); setRevsOpen(r); };
  const openEdit = (r) => {
    setEditing(r);
    setF({
      product_name: r.product_name || r.name || "",
      customer_id: r.customer_id || "",
      unit_ml: r.unit_ml || 100,
      working_quantity_kg: r.working_quantity_kg ?? null,
      working_unit: r.working_unit || "g",
      lines: (r.lines || []).map(l => ({
        material_id: l.material_id || null,
        material_name: l.material_name || "",
        is_manual: !!l.is_manual,
        unit: (l.unit || "kg"),
        percentage: l.percentage || 0,
        qty_adet: l.qty_adet || 0,
        manual_cost: l.manual_cost || 0,
        manual_currency: l.manual_currency || "EUR",
        notes: l.notes || "",
      })),
      notes: r.notes || "",
      sample_status: r.sample_status || "processing",
      sample_delivery_date: r.sample_delivery_date || "",
      revision_note: "",
    });
    setOpen(true);
  };

  const openSample = (r) => { setSampleModal(r); setSampleForm({ sample_status: r.sample_status || "processing", sample_delivery_date: r.sample_delivery_date || "" }); };
  // iter265 — Quick set sample status directly from list row (icon-only actions).
  // processing/ready: doğrudan API çağrısı. delivered: küçük "Teslim Tarihi" prompt.
  const [deliveryPrompt, setDeliveryPrompt] = useState(null); // { row, date }
  const quickSetSample = async (r, status) => {
    if (status === "delivered") {
      setDeliveryPrompt({ row: r, date: (r.sample_delivery_date || new Date().toISOString().slice(0,10)) });
      return;
    }
    try {
      await api.post(`/rd-samples/${r.id}/sample-status`, { sample_status: status, sample_delivery_date: "" });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const submitDeliveryPrompt = async () => {
    if (!deliveryPrompt?.date) { alert("Teslim tarihini girin"); return; }
    try {
      await api.post(`/rd-samples/${deliveryPrompt.row.id}/sample-status`, {
        sample_status: "delivered", sample_delivery_date: deliveryPrompt.date,
      });
      setDeliveryPrompt(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const saveSample = async () => {
    if (sampleForm.sample_status === "delivered" && !sampleForm.sample_delivery_date) { alert("Teslim tarihini girin"); return; }
    try {
      await api.post(`/rd-samples/${sampleModal.id}/sample-status`, sampleForm);
      setSampleModal(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };

  const startTransfer = (r) => {
    setTransferOpen(r);
    setTransferWarn(null);
    setTform({ customer_id: r.customer_id || "", brand_id: "", product_id: "", version: "v1", useNewProduct: true, new_product: { name: r.product_name || r.name || "", fill_qty: r.unit_ml || 100, fill_unit: "ml", units_per_carton: 24, shelf_life_days: 730 }, confirm_manual: false });
  };
  const transfer = async (force = false) => {
    const payload = { customer_id: tform.customer_id, brand_id: tform.brand_id, version: tform.version, confirm_manual: !!force };
    if (tform.useNewProduct) {
      if (!tform.new_product.name.trim()) { alert("Yeni ürün adı zorunlu"); return; }
      payload.new_product = tform.new_product;
    } else {
      if (!tform.product_id) { alert("Ürün seçin veya yeni ürün oluşturun"); return; }
      payload.product_id = tform.product_id;
    }
    try {
      const res = await api.post(`/rd-samples/${transferOpen.id}/transfer`, payload);
      if (res && res.requires_confirmation) {
        setTransferWarn(res);
        return;
      }
      setTransferOpen(null); setTransferWarn(null); load();
      alert(`Üretim formülü oluşturuldu.${res.manual_material_count ? ` (${res.manual_material_count} manuel Ar-Ge malzemesi işaretlendi)` : ""}`);
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };

  const openPdf = async (id) => {
    try { await api.download(`/printables/rd/${id}/pdf`, `arge_${id}.pdf`); }
    catch { alert("PDF oluşturulamadı"); }
  };

  // iter258 — 4-up 100×100 mm thermal label (2×2 grid of 50×30 mm labels).
  // iter260 — Opens an in-app modal preview (iframe) instead of a new tab.
  // iter261 — Copy count selector (1 / 2 / 4) reuses the same 100×100 mm sheet.
  const openLabel = async (id, count = 4) => {
    try {
      const token = localStorage.getItem("erp_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/rd-samples/${id}/label?count=${count}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt.slice(0, 200) || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      setLabelPreview(prev => {
        // Revoke previous blob so we don't leak on count-change refetches
        if (prev?.url) { try { URL.revokeObjectURL(prev.url); } catch {} }
        return { url: objectUrl, id, count };
      });
    } catch (e) {
      alert(e.message || "Etiket açılamadı");
    }
  };
  const closeLabelPreview = () => {
    if (labelPreview?.url) {
      try { URL.revokeObjectURL(labelPreview.url); } catch {}
    }
    setLabelPreview(null);
  };
  const printLabelPreview = () => {
    const iframe = document.getElementById("rd-label-iframe");
    if (iframe && iframe.contentWindow) {
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
      catch (e) { alert("Yazdırma başlatılamadı: " + (e.message || e)); }
    }
  };

  // -------- List rendering helpers --------
  const cname = id => customers.find(c => c.id === id)?.legal_name || "—";
  const visibleRows = filteredRows.length > 0 || rows.length === 0 ? filteredRows : rows;
  const pg = usePagination(visibleRows, "rd-samples", 100);
  const pagedRows = pg.paged;
  const allChecked = pagedRows.length > 0 && pagedRows.every(r => selectedIds[r.id]);
  const toggleAll = () => {
    if (allChecked) setSelectedIds({});
    else { const s = {}; pagedRows.forEach(r => s[r.id] = true); setSelectedIds(s); }
  };
  const selectedCount = Object.values(selectedIds).filter(Boolean).length;
  const downloadSelectedPdf = async () => {
    if (selectedCount === 0) { alert("En az bir kayıt seçin"); return; }
    try {
      const params = new URLSearchParams();
      const blob = await api.getBlob(`/reports/rd_samples?${params.toString()}`);
      openPdfPreview(blob, "arge_projeleri.pdf");
    } catch { alert("PDF oluşturulamadı"); }
  };

  return (
    <div>
      <PageHeader title="Ar-Ge Portalı" description="Deneysel formüller, revizyonlar, onay ve üretime aktarma" actions={
        <div className="flex gap-2">
          {selectedCount > 0 && <Btn variant="secondary" onClick={downloadSelectedPdf} data-testid="rd-selected-pdf"><Download size={14}/> Seçili ({selectedCount}) PDF</Btn>}
          <Btn
            onClick={() => setAiOpen(true)}
            className="bg-gradient-to-r from-violet-600 via-fuchsia-500 to-pink-500 text-white shadow-md hover:shadow-lg"
            data-testid="rd-ai-open">
            <Sparkles size={14}/> Yapay Zeka ile Proje Oluştur
          </Btn>
          <Btn onClick={() => { setEditing(null); setF({ ...empty, lines: [] }); setOpen(true); }} data-testid="new-rd"><Plus size={14}/> Yeni Çalışma</Btn>
        </div>
      } />
      <MeetingsBanner department="RND" />
      <FilterAndReportBar
        reportType="rd_samples" rows={rows} customers={customers}
        statuses={[{value:"pending",label:"Bekliyor"},{value:"approved",label:"Onaylı"},{value:"rejected",label:"Reddedildi"}]}
        dateAccessor={r => r.created_at}
        customerAccessor={r => r.customer_id}
        productAccessor={r => r.product_name || r.name || r.project_no || ""}
        statusAccessor={r => r.status}
        onFiltered={setFilteredRows}
      />
      <div className="overflow-x-auto bg-white border border-slate-200 rounded-lg">
        <table className="w-full text-sm" data-testid="rd-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-2 py-2 w-8"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="rd-check-all"/></th>
              {["Proje No", "Ürün Adı", "Müşteri", "Revizyon", ...(isAdmin ? ["Maliyet / kg"] : []), "Numune", "Onay", "İşlem"].map((h, i) =>
                <th key={i} className="text-left px-2 py-2 text-[11px] font-semibold text-slate-600 uppercase tracking-wide whitespace-nowrap">{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {pagedRows.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-slate-400">Kayıt yok</td></tr>}
            {pagedRows.map(r => {
              const c = r.cost || {};
              return (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-2 py-1.5"><input type="checkbox" checked={!!selectedIds[r.id]} onChange={e => setSelectedIds({ ...selectedIds, [r.id]: e.target.checked })} data-testid={`rd-check-${r.id}`}/></td>
                  <td className="px-2 py-1.5 font-mono text-xs">
                    <button className="text-indigo-700 hover:underline" onClick={() => setDetailOpen(r)} data-testid={`rd-proj-${r.id}`}>{r.project_no || "—"}</button>
                  </td>
                  <td className="px-2 py-1.5">{r.product_name || r.name}</td>
                  <td className="px-2 py-1.5">{cname(r.customer_id)}</td>
                  <td className="px-2 py-1.5 font-mono text-xs">
                    <button className="text-slate-600 hover:text-indigo-700" onClick={() => showRevs(r)} data-testid={`revs-${r.id}`} title="Revizyon geçmişi"><History size={12} className="inline"/> {r.revision || "R00"}</button>
                  </td>
                  {isAdmin && <td className="px-2 py-1.5 font-mono">€{(c.cost_per_kg_eur ?? c.cost_per_kg ?? 0).toFixed(2)} / kg</td>}
                  <td className="px-2 py-1.5">
                    {/* iter294c — Sadece iconlar (metin badge kaldirildi).
                        Play/Package/Truck aktif duruma gore renklenir; ayni
                        zamanda tikla-degistir aksiyonu icin butonlardir. */}
                    <div className="inline-flex gap-0.5 align-middle" onClick={(e) => e.stopPropagation()}>
                      <button title="İşlemde"
                        className={`p-1 rounded ${r.sample_status==="processing"?"bg-orange-100 text-orange-700":"text-slate-400 hover:bg-slate-100"}`}
                        onClick={() => quickSetSample(r, "processing")}
                        data-testid={`sample-set-processing-${r.id}`}><Play size={13}/></button>
                      <button title="Numune Hazır"
                        className={`p-1 rounded ${r.sample_status==="ready"?"bg-yellow-100 text-yellow-800":"text-slate-400 hover:bg-slate-100"}`}
                        onClick={() => quickSetSample(r, "ready")}
                        data-testid={`sample-set-ready-${r.id}`}><Package size={13}/></button>
                      <button title="Teslim Edildi"
                        className={`p-1 rounded ${r.sample_status==="delivered"?"bg-emerald-100 text-emerald-700":"text-slate-400 hover:bg-slate-100"}`}
                        onClick={() => quickSetSample(r, "delivered")}
                        data-testid={`sample-set-delivered-${r.id}`}><Truck size={13}/></button>
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    {/* iter294c — Onay durumu icon-only. Beklemede=Clock (amber),
                        Onaylandi=CheckCircle (yesil), Onaylanmadi=XCircle (kirmizi). */}
                    {(() => {
                      const s = r.status || "pending";
                      const cfg = s === "approved"
                        ? { Icon: CheckCircle, cls: "bg-emerald-100 text-emerald-700 ring-emerald-600/20", title: "Onaylandı" }
                        : s === "rejected"
                        ? { Icon: XCircle, cls: "bg-red-100 text-red-700 ring-red-600/20", title: "Onaylanmadı" }
                        : { Icon: Clock, cls: "bg-orange-100 text-orange-700 ring-orange-600/20", title: "Beklemede" };
                      const IconComp = cfg.Icon;
                      return (
                        <span title={cfg.title} data-testid={`approval-icon-${r.id}`}
                              className={`inline-flex items-center justify-center rounded-md p-1 ring-1 ring-inset ${cfg.cls}`}>
                          <IconComp size={14}/>
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
                      {(isAdmin || r.status !== "approved") && <Btn size="sm" variant="secondary" onClick={() => openEdit(r)} data-testid={`edit-rd-${r.id}`} title="Düzenle"><Edit3 size={12}/></Btn>}
                      {r.status === "pending" && <Btn size="sm" variant="approve" onClick={() => decide(r.id, true)} data-testid={`approve-rd-${r.id}`} title="Onayla"><CheckCircle size={12}/></Btn>}
                      {r.status === "pending" && <Btn size="sm" variant="reject" onClick={() => { setRejectOpen(r); setRejectReason(""); }} data-testid={`reject-rd-${r.id}`} title="Reddet"><XCircle size={12}/></Btn>}
                      {r.status === "approved" && !r.transferred_formula_id && <Btn size="sm" onClick={() => startTransfer(r)} data-testid={`transfer-rd-${r.id}`} title="Formüllere Aktar"><ArrowRightCircle size={12}/></Btn>}
                      {r.status === "approved" && r.transferred_formula_id && <Badge level="ok" testid={`transferred-${r.id}`} title="Formüllere Aktarıldı"><CheckCircle size={11}/></Badge>}
                      <Btn size="sm" variant="secondary" onClick={() => setAiContentOpen(r)} data-testid={`rd-ai-content-${r.id}`} title="AI İçerik"><Wand2 size={12}/></Btn>
                      <Btn size="sm" variant="secondary" onClick={() => setComplianceFor(r)} data-testid={`rd-compliance-${r.id}`} title="Mevzuata Göre Kontrol Et"><ShieldCheck size={12}/></Btn>
                      <Btn size="sm" variant="secondary" onClick={() => openLabel(r.id)} data-testid={`label-rd-${r.id}`} title="Etiket Yazdır (4-lü 50×30 mm)"><Tag size={12}/></Btn>
                      <Btn size="sm" variant="secondary" onClick={() => copy(r.id)} data-testid={`copy-rd-${r.id}`} title="Kopyala"><Copy size={12}/></Btn>
                      {isAdmin && <Btn size="sm" variant="danger" onClick={() => del(r.id)} data-testid={`del-rd-${r.id}`} title="Sil"><Trash2 size={12}/></Btn>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ListPagination pagination={pg} testid="rd-pagination"/>

      {/* iter162 — AI Ürün İçerik Asistanı (Etiket / Tanıtım / Pazarlama / Claim / Sosyal Medya).
           Formül üretici DEĞİL: mevcut onaylı formülü referans alarak ürün tanıtım metinleri üretir. */}
      <Modal open={!!aiContentOpen} onClose={() => setAiContentOpen(null)} title={aiContentOpen ? `AI Ürün İçerik Asistanı — ${aiContentOpen.project_no || aiContentOpen.code} · ${aiContentOpen.revision || "R00"}` : ""} size="xl">
        {aiContentOpen && <AIContentAssistant sample={aiContentOpen} onClose={() => setAiContentOpen(null)} />}
      </Modal>

      {/* iter188 — Mevzuata Göre Kontrol Et (Formulas.jsx ile aynı shared modal).
           Ar-Ge projesi Formüllere aktarıldıysa (transferred_formula_id) kontrol
           yapılır; aksi halde modal, önce Formüllere aktarım gerektiğini uyarır. */}
      <RegulatoryCheckModal
        open={!!complianceFor}
        target={complianceFor && {
          id: complianceFor.id,
          formula_id: complianceFor.transferred_formula_id || null,
          revision: complianceFor.revision,
          customer_id: complianceFor.customer_id,
          product_id: complianceFor.product_id,
          project_no: complianceFor.project_no,
          code: complianceFor.code,
          product_name: complianceFor.product_name,
        }}
        onClose={() => setComplianceFor(null)}
      />
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? `Ar-Ge Projesi Düzenle — ${editing.project_no}` : "Yeni Proje"} size="full">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Input label="Proje No" value={editing?.project_no || "(otomatik)"} disabled data-testid="rd-project-no"/>
          <Input label="Ürün Adı *" value={f.product_name} onChange={e => setF({ ...f, product_name: e.target.value })} data-testid="rd-product-name"/>
          <div>
            <SmartSuggestInput
              label="Müşteri *"
              value={f.customer_name || (customers.find(c=>c.id===f.customer_id)?.legal_name || "")}
              selectedId={f.customer_id}
              items={customers}
              displayField="legal_name"
              idField="id"
              secondaryField={(c) => [c.code && `Kod: ${c.code}`, c.tax_id && `VKN: ${c.tax_id}`, c.city].filter(Boolean).join(" · ")}
              placeholder="Müşteri arayın veya yeni ad yazın..."
              onChange={({ value, selectedId }) => setF({ ...f, customer_id: selectedId || "", customer_name: value })}
              emptyAction={{
                label: `"${(f.customer_name || "").trim()}" değerini yeni müşteri olarak kaydet`,
                onClick: async (typed) => {
                  if (!typed) return;
                  // Iter218 promote-to-master: reuses existing POST /customers minimal contract.
                  try {
                    const created = await api.post("/customers", { legal_name: typed });
                    setF(prev => ({ ...prev, customer_id: created?.id || "", customer_name: created?.legal_name || typed }));
                    // reload customer list
                    api.get("/customers").then(setCustomers);
                  } catch (e) {
                    alert(e.response?.data?.detail || "Müşteri oluşturulamadı");
                  }
                },
              }}
              data-testid="rd-customer"
            />
          </div>
          <Input label="Ürün Miktarı (ml)" type="number" step="1" value={f.unit_ml || ""} onChange={e => setF({ ...f, unit_ml: parseFloat(e.target.value) || 0 })} data-testid="rd-unit-ml"/>
        </div>

        {/* Iter243 (Faz 3B) — Çalışılacak Miktar (canonical kg saklanır, gösterim g/kg). */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3 p-3 bg-amber-50/50 border border-amber-200 rounded" data-testid="rd-working-block">
          <div>
            <label className="text-[10px] uppercase font-semibold text-slate-600 tracking-wide block">Çalışılacak / Numune Miktarı</label>
            <NumInput
              step="0.001"
              value={(() => {
                const kg = f.working_quantity_kg;
                if (kg === null || kg === undefined || kg === "") return "";
                return (f.working_unit || "g") === "kg" ? Number(kg) : Number(kg) * 1000;
              })()}
              onChange={e => {
                const v = e.target.value;
                if (v === "" || v === null) { setF({ ...f, working_quantity_kg: null }); return; }
                const num = parseFloat(v);
                if (isNaN(num)) { setF({ ...f, working_quantity_kg: null }); return; }
                const kg = (f.working_unit || "g") === "kg" ? num : num / 1000;
                setF({ ...f, working_quantity_kg: kg });
              }}
              placeholder="ör. 500"
              className="w-full mt-1 px-2 py-1 border rounded text-sm"
              data-testid="rd-working-qty"/>
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold text-slate-600 tracking-wide block">Birim</label>
            <select
              value={f.working_unit || "g"}
              onChange={e => setF({ ...f, working_unit: e.target.value })}
              className="w-full mt-1 px-2 py-1 border rounded text-sm"
              data-testid="rd-working-unit">
              <option value="g">g</option>
              <option value="kg">kg</option>
            </select>
            <div className="text-[10px] text-slate-500 mt-1">Birim değişikliği miktarı fiziksel olarak dönüştürür.</div>
          </div>
          <div className="text-sm">
            <span className="text-slate-500 uppercase text-[10px] block">Canonical (kg)</span>
            <span className="font-mono font-semibold text-amber-800" data-testid="rd-working-canonical">
              {f.working_quantity_kg == null ? "—" : `${Number(f.working_quantity_kg).toFixed(4)} kg`}
            </span>
          </div>
        </div>

        {/* [msg-145 Bölüm 3+4] Maliyet/kg + Birim Ürün Maliyeti yalnızca Sistem Yöneticisi'ne görünür. */}
        <div className={`grid ${isAdmin ? "grid-cols-3" : "grid-cols-1"} gap-3 mb-3 p-3 bg-indigo-50/50 border border-indigo-100 rounded`}>
          {isAdmin && <div className="text-sm"><span className="text-slate-500 uppercase text-[10px] block">Maliyet / kg</span><span className="font-mono font-semibold text-indigo-700" data-testid="rd-cost-per-kg">€{costPerKg.toFixed(4)} / kg</span></div>}
          {isAdmin && <div className="text-sm"><span className="text-slate-500 uppercase text-[10px] block">Birim Ürün Maliyeti ({f.unit_ml || 0} ml)</span><span className="font-mono font-semibold text-emerald-700" data-testid="rd-unit-cost">€{unitCostEur.toFixed(4)}</span></div>}
          <div className="text-sm"><span className="text-slate-500 uppercase text-[10px] block">Toplam Formül %</span><span className={`font-mono font-semibold ${Math.abs(totalPct - 100) > 0.01 ? "text-amber-600" : "text-emerald-600"}`} data-testid="rd-total-pct">{totalPct.toFixed(2)}%</span></div>
        </div>

        <div className="border rounded p-3 bg-slate-50/50 mb-3">
          <div className="flex justify-between mb-2"><div className="text-sm font-semibold"><FlaskConical size={14} className="inline"/> Bileşenler</div></div>
          <div className="grid grid-cols-12 gap-2 mb-1 text-[10px] uppercase text-slate-500 px-1">
            <div className="col-span-4">Malzeme Adı</div>
            <div className="col-span-1">Birim</div>
            <div className="col-span-1">% / Adet</div>
            <div className="col-span-1">Kullanılacak</div>
            <div className="col-span-2">Fiyat</div>
            <div className="col-span-2">Notlar</div>
            <div className="col-span-1"></div>
          </div>
          {(f.lines || []).map((l, i) => {
            const dropAbove = linesDnd.isDropTarget(i) && linesDnd.direction() === "up";
            const dropBelow = linesDnd.isDropTarget(i) && linesDnd.direction() === "down";
            const dragging = linesDnd.isDragging(i);
            return (
            <div key={i} className={`flex items-stretch gap-1 mb-1 relative transition-all ${dragging ? "opacity-50" : ""} ${linesDnd.isDropTarget(i) ? "bg-indigo-50/60 rounded" : ""}`} {...linesDnd.rowProps(i)} data-testid={`rd-row-${i}`}>
              {dropAbove && <div className="absolute -top-0.5 left-6 right-0 h-0.5 border-t-2 border-dashed border-indigo-500 pointer-events-none" data-testid={`rd-drop-above-${i}`}/>}
              {dropBelow && <div className="absolute -bottom-0.5 left-6 right-0 h-0.5 border-b-2 border-dashed border-indigo-500 pointer-events-none" data-testid={`rd-drop-below-${i}`}/>}
              <button type="button" draggable {...linesDnd.handleProps(i)}
                className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-indigo-600 flex items-center px-1 rounded hover:bg-slate-100 shrink-0"
                title="Sırayı değiştirmek için sürükleyin"
                data-testid={`rd-drag-${i}`}
                aria-label="Satırı sürükle"
              ><GripVertical size={14}/></button>
              <div className={`grid grid-cols-12 gap-2 flex-1 bg-white border rounded p-1 ${l.is_manual ? "border-amber-300 bg-amber-50/40" : ""}`}>
              <div className="col-span-4">
                <MaterialAutocomplete
                  value={l.material_name}
                  materials={materials}
                  onChangeText={(name) => changeLineText(i, name)}
                  onSelectExisting={(m) => pickExisting(i, m)}
                  testid={`rd-mat-${i}`}
                />
                {l.is_manual && <div className="text-[10px] text-amber-700 mt-0.5">Manuel Ar-Ge malzemesi (envanterde yok)</div>}
              </div>
              <select value={l.unit || "kg"} onChange={e => updLine(i, { unit: e.target.value })} className="col-span-1 px-1 py-1 text-xs border rounded" data-testid={`rd-unit-${i}`} disabled={!l.is_manual && l.material_id}>
                <option value="kg">kg</option><option value="adet">adet</option>
              </select>
              {l.unit === "kg" ? (
                <NumInput step="0.001" value={l.percentage || 0} onChange={e => updLine(i, { percentage: parseFloat(e.target.value) || 0 })} placeholder="%" className="col-span-1 px-2 py-1 text-xs border rounded" data-testid={`rd-pct-${i}`}/>
              ) : (
                <NumInput step="1" value={l.qty_adet || 0} onChange={e => updLine(i, { qty_adet: parseFloat(e.target.value) || 0 })} placeholder="Adet" className="col-span-1 px-2 py-1 text-xs border rounded" data-testid={`rd-adet-${i}`}/>
              )}
              {/* Iter243 (Faz 3B) — Kullanılacak Miktar per line, unit-aware. */}
              <div className="col-span-1 text-[11px] pt-1.5 font-mono" data-testid={`rd-used-${i}`}>
                {(() => {
                  const wq_kg = f.working_quantity_kg;
                  if (wq_kg === null || wq_kg === undefined || wq_kg === "") {
                    return <span className="text-slate-400">—</span>;
                  }
                  if ((l.unit || "kg") === "kg") {
                    const used_kg = Number(wq_kg) * ((parseFloat(l.percentage) || 0) / 100);
                    const disp = (f.working_unit || "g") === "kg"
                      ? `${used_kg.toFixed(4)} kg`
                      : `${(used_kg * 1000).toFixed(2)} g`;
                    return <span className="text-emerald-700">{disp}</span>;
                  }
                  return <span className="text-emerald-700">{(parseFloat(l.qty_adet) || 0).toFixed(0)} adet</span>;
                })()}
              </div>
              {/* Fiyat: manuel satırda düzenlenebilir, envanter satırında salt-okunur */}
              {l.is_manual ? (
                <div className="col-span-2 flex gap-1">
                  <NumInput step="0.01" value={l.manual_cost || 0} onChange={e => updLine(i, { manual_cost: parseFloat(e.target.value) || 0 })} placeholder="Birim" className="flex-1 min-w-0 px-2 py-1 text-xs border rounded" data-testid={`rd-mcost-${i}`}/>
                  <select value={l.manual_currency || "EUR"} onChange={e => updLine(i, { manual_currency: e.target.value })} className="px-1 py-1 text-xs border rounded" data-testid={`rd-mcur-${i}`}>
                    <option value="EUR">€</option><option value="TRY">₺</option><option value="USD">$</option>
                  </select>
                </div>
              ) : (
                <div className="col-span-2 text-[11px] pt-1.5 font-mono text-slate-600 truncate" data-testid={`rd-inv-cost-${i}`}>
                  {(() => {
                    if (!l.material_id) return <span className="text-slate-400">—</span>;
                    const m = materials.find(x => x.id === l.material_id);
                    if (!m) return <span className="text-slate-400">—</span>;
                    const sym = (m.currency || "TRY").toUpperCase() === "EUR" ? "€" : ((m.currency || "TRY").toUpperCase() === "USD" ? "$" : "₺");
                    return <span>{sym}{Number(m.unit_cost || 0).toFixed(4)}</span>;
                  })()}
                </div>
              )}
              <input value={l.notes || ""} onChange={e => updLine(i, { notes: e.target.value })} placeholder="ör. 40°C altında ekle" className="col-span-2 px-2 py-1 text-xs border rounded" data-testid={`rd-notes-${i}`}/>
              <div className="col-span-1 flex items-center gap-0.5 justify-end">
                <button type="button" onClick={() => linesDnd.moveUp(i)} disabled={i === 0} className="text-slate-500 hover:text-indigo-700 disabled:opacity-30 disabled:cursor-not-allowed p-0.5" title="Yukarı taşı" data-testid={`rd-up-${i}`}><ChevronUp size={14}/></button>
                <button type="button" onClick={() => linesDnd.moveDown(i)} disabled={i === (f.lines || []).length - 1} className="text-slate-500 hover:text-indigo-700 disabled:opacity-30 disabled:cursor-not-allowed p-0.5" title="Aşağı taşı" data-testid={`rd-down-${i}`}><ChevronDown size={14}/></button>
                <button onClick={() => rmLine(i)} className="text-red-500 text-xs px-1 hover:underline" data-testid={`rd-del-${i}`} title="Sil">Sil</button>
              </div>
              </div>
            </div>
          );})}
          <Btn size="sm" variant="secondary" onMouseDown={(e) => document.activeElement?.blur()} onClick={addLine} data-testid="add-rd-line"><Plus size={12}/> Malzeme Ekle</Btn>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <Select label="Numune Durumu" value={f.sample_status} onChange={e => setF({ ...f, sample_status: e.target.value })} data-testid="rd-sample-status">
            <option value="processing">İşlemde</option><option value="ready">Numune Hazır</option><option value="delivered">Teslim Edildi</option>
          </Select>
          {f.sample_status === "delivered" && <Input label="Teslim Tarihi" type="date" value={(f.sample_delivery_date || "").slice(0,10)} onChange={e => setF({ ...f, sample_delivery_date: e.target.value })} data-testid="rd-delivery-date"/>}
          {editing && <Input label="Revizyon Notu" value={f.revision_note || ""} onChange={e => setF({ ...f, revision_note: e.target.value })} placeholder="ör. Yüzde değişikliği" data-testid="rd-rev-note"/>}
        </div>
        <Textarea label="Notlar" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} data-testid="rd-notes"/>
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn>
          <Btn onClick={save} data-testid="save-rd">{editing ? "Güncelle (Yeni Revizyon)" : "Kaydet"}</Btn>
        </div>
      </Modal>

      {/* ---------- Revision history ---------- */}
      <Modal open={!!revsOpen} onClose={() => setRevsOpen(null)} title={`Revizyon Geçmişi — ${revsOpen?.project_no || ""}`} size="lg">
        {revs.length === 0 && <div className="text-slate-400 text-sm">Revizyon kaydı yok</div>}
        {/* iter294d — Ardışık revizyonlar arasındaki hammadde farkını göster.
             Backend revs desc (en yeni ilk) döner; her r için bir sonraki
             eleman (r_next) daha eski revizyondur → diff(r_next.lines, r.lines). */}
        {revs.map((r, i) => {
          const prevSnap = revs[i + 1]?.snapshot || null;
          const currSnap = r.snapshot || {};
          const isFirst = i === revs.length - 1;
          const diffs = isFirst ? [] : diffRevisionLines(prevSnap?.lines || [], currSnap.lines || []);
          return (
            <div key={r.id} className="border-l-2 border-indigo-400 pl-3 py-2 mb-2" data-testid={`rd-rev-${r.revision}`}>
              <div className="text-sm font-semibold">{r.revision}</div>
              <div className="text-xs text-slate-500">{(r.at || "").slice(0, 19)} — {r.by_name || ""}</div>
              {r.note && <div className="text-xs text-slate-600 italic">"{r.note}"</div>}
              {isFirst ? (
                <div className="text-[11px] text-slate-400 mt-1">İlk kayıt (referans revizyon)</div>
              ) : diffs.length === 0 ? (
                <div className="text-[11px] text-slate-400 mt-1">Hammadde değişikliği yok (yalnız meta/not güncellendi)</div>
              ) : (
                <ul className="mt-1.5 flex flex-wrap gap-1" data-testid={`rd-rev-diff-${r.revision}`}>
                  {diffs.map((d, k) => (
                    <li key={k} className={`text-[11px] px-1.5 py-0.5 rounded border ${diffKindClass(d.kind)}`}>{d.text}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </Modal>

      {/* ---------- Detail (project no click) ---------- */}
      <Modal open={!!detailOpen} onClose={() => setDetailOpen(null)} title={`Ar-Ge Proje Detayı — ${detailOpen?.project_no || ""} (${detailOpen?.revision || "R00"})`} size="xl">
        <DetailView project={detailOpen} customers={customers} onClose={() => setDetailOpen(null)} onPdf={() => openPdf(detailOpen?.id)} onLabel={() => openLabel(detailOpen?.id)}/>
      </Modal>

      {/* ---------- iter260/iter261: R&D 4-lü Etiket Önizleme (modal iframe, 1/2/4 kopya) ---------- */}
      <Modal open={!!labelPreview} onClose={closeLabelPreview} title="Ar-Ge Etiket Önizleme (100×100 mm termal kağıt)" size="xl">
        {labelPreview && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-600 font-medium">Kopya adedi:</span>
                {[1, 2, 4].map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => openLabel(labelPreview.id, n)}
                    className={`px-3 py-1 text-xs rounded border ${labelPreview.count === n ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                    data-testid={`rd-label-count-${n}`}
                  >
                    {n === 1 ? "1 Etiket" : n === 2 ? "2 Etiket (Üst Sıra)" : "4 Etiket (2×2)"}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <Btn size="sm" variant="secondary" onClick={printLabelPreview} data-testid="rd-label-preview-print"><Tag size={14}/> Yazdır</Btn>
                <Btn size="sm" variant="secondary" onClick={closeLabelPreview} data-testid="rd-label-preview-close">Kapat</Btn>
              </div>
            </div>
            <div className="border rounded bg-slate-50 flex justify-center">
              <iframe
                id="rd-label-iframe"
                title="R&D Etiket Önizleme"
                src={labelPreview.url}
                className="w-full"
                style={{ height: "70vh", border: 0, background: "white" }}
                data-testid="rd-label-iframe"
              />
            </div>
            <div className="text-[11px] text-slate-500">Aynı 100×100 mm termal kağıda basılır — boş kalan gözler otomatik boşluk olarak korunur. Yazıcı ayarlarında kağıt boyutu "100×100 mm" seçili olmalı.</div>
          </div>
        )}
      </Modal>

      {/* ---------- Sample status ---------- */}
      <Modal open={!!sampleModal} onClose={() => setSampleModal(null)} title="Numune Durumu Güncelle" size="md">
        <div className="space-y-3">
          <Select label="Numune Durumu" value={sampleForm.sample_status} onChange={e => setSampleForm({ ...sampleForm, sample_status: e.target.value })} data-testid="sample-status-select">
            <option value="processing">İşlemde</option><option value="ready">Numune Hazır</option><option value="delivered">Teslim Edildi</option>
          </Select>
          {sampleForm.sample_status === "delivered" && <Input label="Teslim Tarihi *" type="date" value={(sampleForm.sample_delivery_date||"").slice(0,10)} onChange={e => setSampleForm({ ...sampleForm, sample_delivery_date: e.target.value })} data-testid="sample-date"/>}
          <div className="flex justify-end gap-2"><Btn variant="secondary" onClick={() => setSampleModal(null)}>Vazgeç</Btn><Btn onClick={saveSample} data-testid="save-sample-status">Kaydet</Btn></div>
        </div>
      </Modal>

      {/* iter265 — Hızlı Teslim Tarihi prompt (list satırından Truck ikonu ile açılır) */}
      <Modal open={!!deliveryPrompt} onClose={() => setDeliveryPrompt(null)} title={`Teslim Tarihi — ${deliveryPrompt?.row?.project_no || ""}`} size="sm">
        <div className="space-y-2">
          <div className="text-sm text-slate-600">Numune "<b>Teslim Edildi</b>" olarak işaretlenecek. Teslim tarihini girin:</div>
          <Input label="Teslim Tarihi *" type="date" value={deliveryPrompt?.date || ""} onChange={e => setDeliveryPrompt({ ...deliveryPrompt, date: e.target.value })} data-testid="delivery-prompt-date"/>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Btn variant="secondary" onClick={() => setDeliveryPrompt(null)}>Vazgeç</Btn>
          <Btn onClick={submitDeliveryPrompt} data-testid="delivery-prompt-save"><Truck size={12}/> Teslim Et</Btn>
        </div>
      </Modal>


      {/* ---------- Reject reason ---------- */}
      <Modal open={!!rejectOpen} onClose={() => setRejectOpen(null)} title={`Reddet — ${rejectOpen?.project_no || ""}`} size="md">
        <Textarea label="Red Nedeni" value={rejectReason} onChange={e => setRejectReason(e.target.value)} data-testid="reject-reason"/>
        <div className="flex justify-end gap-2 mt-3"><Btn variant="secondary" onClick={() => setRejectOpen(null)}>Vazgeç</Btn><Btn variant="reject" onClick={() => decide(rejectOpen.id, false, rejectReason)} data-testid="confirm-reject">Reddet</Btn></div>
      </Modal>

      {/* ---------- Transfer to Formulas ---------- */}
      <Modal open={!!transferOpen} onClose={() => { setTransferOpen(null); setTransferWarn(null); }} title="Formüllere Aktar" size="lg">
        {transferWarn && (
          <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded text-sm">
            <div className="font-semibold text-amber-800">⚠ {transferWarn.message}</div>
            <ul className="list-disc pl-5 mt-1 text-amber-800">
              {(transferWarn.manual_materials || []).map((m, i) => <li key={i}>{m.name} ({m.unit})</li>)}
            </ul>
            <div className="text-xs text-amber-700 mt-1">Bu malzemeler formülde <b>Envanter Kaydı Eksik</b> olarak işaretlenecek — envantere otomatik eklenmeyecek.</div>
          </div>
        )}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <SmartSuggestInput
              label="Müşteri *"
              value={customers.find(c => c.id === tform.customer_id)?.legal_name || ""}
              selectedId={tform.customer_id}
              items={customers}
              displayField="legal_name"
              idField="id"
              secondaryField={(c) => [c.code && `Kod: ${c.code}`, c.tax_id && `VKN: ${c.tax_id}`].filter(Boolean).join(" · ")}
              placeholder="Müşteri seçin..."
              onChange={({ selectedId }) => setTform({ ...tform, customer_id: selectedId || "", brand_id: "", product_id: "" })}
              data-testid="transfer-customer"
            />
            <Select label="Marka *" value={tform.brand_id} onChange={e => setTform({ ...tform, brand_id: e.target.value, product_id: "" })} data-testid="transfer-brand">
              <option value="">—</option>{brands.filter(b => !tform.customer_id || b.customer_id === tform.customer_id).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Select>
          </div>
          <div className="flex items-center gap-3 border-t border-slate-200 pt-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" checked={!tform.useNewProduct} onChange={() => setTform({ ...tform, useNewProduct: false })}/>
              Mevcut ürün seç
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" checked={tform.useNewProduct} onChange={() => setTform({ ...tform, useNewProduct: true })}/>
              Yeni ürün oluştur
            </label>
          </div>
          {!tform.useNewProduct ? (
            <Select label="Ürün *" value={tform.product_id} onChange={e => setTform({ ...tform, product_id: e.target.value })} data-testid="transfer-product">
              <option value="">—</option>{products.filter(p => !tform.brand_id || p.brand_id === tform.brand_id).map(p => <option key={p.id} value={p.id}>{p.name} ({p.fill_qty}{p.fill_unit})</option>)}
            </Select>
          ) : (
            <div className="grid grid-cols-2 gap-3 p-3 bg-fuchsia-50/40 border border-fuchsia-200 rounded">
              <Input label="Ürün Adı *" value={tform.new_product.name} onChange={e => setTform({ ...tform, new_product: { ...tform.new_product, name: e.target.value } })} data-testid="np-name"/>
              <Input label="Dolum" type="number" value={tform.new_product.fill_qty} onChange={e => setTform({ ...tform, new_product: { ...tform.new_product, fill_qty: parseFloat(e.target.value) || 100 } })}/>
              <Select label="Birim" value={tform.new_product.fill_unit} onChange={e => setTform({ ...tform, new_product: { ...tform.new_product, fill_unit: e.target.value } })}>
                <option value="ml">ml</option><option value="g">g</option><option value="adet">adet</option>
              </Select>
              <Input label="Karton" type="number" value={tform.new_product.units_per_carton} onChange={e => setTform({ ...tform, new_product: { ...tform.new_product, units_per_carton: parseInt(e.target.value) || 24 } })}/>
              <Input label="Raf (gün)" type="number" value={tform.new_product.shelf_life_days} onChange={e => setTform({ ...tform, new_product: { ...tform.new_product, shelf_life_days: parseInt(e.target.value) || 730 } })}/>
              <Input label="Versiyon" value={tform.version} onChange={e => setTform({ ...tform, version: e.target.value })}/>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => { setTransferOpen(null); setTransferWarn(null); }}>Vazgeç</Btn>
          {transferWarn ? (
            <Btn onClick={() => transfer(true)} data-testid="confirm-transfer-manual">Yine de Aktar</Btn>
          ) : (
            <Btn onClick={() => transfer(false)} data-testid="do-transfer">Formüle Aktar</Btn>
          )}
        </div>
      </Modal>

      {/* ---------- iter122 — AI Ar-Ge Asistanı ---------- */}
      {aiOpen && (
        <AIAssistantModal
          customers={customers}
          onClose={() => setAiOpen(false)}
          onTransferred={() => { setAiOpen(false); load(); }}
        />
      )}
    </div>
  );
}

/* ============================================================
   iter122 — AI Ar-Ge Asistanı (Yapay Zeka ile Proje Oluştur)
   Mevcut `POST /api/rd-samples` endpoint'ini yeniden kullanır.
   Yeni proje numarası / formül tablosu / envanter YOK.
============================================================ */
function AIAssistantModal({ customers, onClose, onTransferred }) {
  const [customerId, setCustomerId] = useState("");
  const [productName, setProductName] = useState("");
  const [unitMl, setUnitMl] = useState(100);
  const [description, setDescription] = useState("");
  const [revisionInstruction, setRevisionInstruction] = useState("");
  const [draft, setDraft] = useState(null);
  const [components, setComponents] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [transferBusy, setTransferBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const transferredRef = useRef(false);

  const generate = async () => {
    if (!description.trim()) { setErr("Lütfen ürünü tarif edin"); return; }
    setBusy(true); setErr("");
    try {
      const r = await api.post("/rd/ai/draft", {
        description: description.trim(),
        unit_ml: Number(unitMl) || 100,
        session_id: sessionId || undefined,
      });
      setSessionId(r.session_id);
      setDraft(r);
      setComponents((r.components || []).map(c => ({ ...c })));
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "AI cevabı alınamadı");
    } finally { setBusy(false); }
  };

  const revise = async () => {
    if (!revisionInstruction.trim()) { setErr("Revizyon talimatınızı yazın"); return; }
    setBusy(true); setErr("");
    try {
      const r = await api.post("/rd/ai/draft", {
        description: description.trim(),
        unit_ml: Number(unitMl) || 100,
        session_id: sessionId || undefined,
        previous_components: components,
        revision_instruction: revisionInstruction.trim(),
      });
      setSessionId(r.session_id);
      setDraft(r);
      setComponents((r.components || []).map(c => ({ ...c })));
      setRevisionInstruction("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Revizyon başarısız");
    } finally { setBusy(false); }
  };

  const updateLine = (i, patch) => setComponents(prev => prev.map((l, j) => j === i ? { ...l, ...patch } : l));
  const removeLine = (i) => setComponents(prev => prev.filter((_, j) => j !== i));

  const kgTotal = components.filter(c => (c.unit || "kg") === "kg")
    .reduce((s, c) => s + (Number(c.percentage) || 0), 0);
  const kgValid = Math.abs(kgTotal - 100) <= 0.5;

  const doTransfer = async () => {
    if (transferredRef.current) return;
    if (!customerId) { setErr("Müşteri seçin"); return; }
    if (!productName.trim()) { setErr("Ürün adı zorunlu"); return; }
    if (!components.length) { setErr("Bileşen yok"); return; }
    if (!kgValid) { setErr(`kg bileşen toplamı %100 olmalı (mevcut %${kgTotal.toFixed(2)})`); return; }
    transferredRef.current = true;
    setTransferBusy(true); setErr("");
    try {
      const rdPayload = {
        product_name: productName.trim(),
        customer_id: customerId,
        unit_ml: Number(unitMl) || 100,
        lines: components.map(c => ({
          material_id: c.is_manual ? null : (c.material_id || null),
          material_name: c.material_name,
          is_manual: !!c.is_manual,
          unit: (c.unit || "kg").toLowerCase(),
          percentage: Number(c.percentage) || 0,
          qty_adet: Number(c.qty_adet) || 0,
          manual_cost: Number(c.manual_cost) || 0,
          manual_currency: c.manual_currency || "EUR",
          notes: [c.function, c.reasoning].filter(Boolean).join(" · ") || null,
        })),
        notes: `[AI ASSISTED DRAFT · ${draft?.provider_used || "ai"}/${draft?.model_used || ""}] ${draft?.product_summary || ""}`.slice(0, 2000),
        sample_status: "processing",
        revision_note: "AI destekli ilk taslak",
      };
      await api.post("/rd-samples", rdPayload);
      onTransferred();
    } catch (e) {
      transferredRef.current = false;
      setErr(e?.response?.data?.detail || e?.message || "Aktarım başarısız");
    } finally { setTransferBusy(false); setConfirmOpen(false); }
  };

  return (
    <Modal open={true} onClose={onClose} title="Yapay Zeka ile Ar-Ge Projesi Oluştur" size="full">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="rd-ai-modal">
        <div className="space-y-3">
          <div className="bg-violet-50/70 border border-violet-200 rounded p-2 text-[11px] text-violet-900 flex items-start gap-2">
            <Sparkles size={14} className="mt-0.5 shrink-0"/>
            <div>
              <b>Referans / Taslak formül.</b> AI, Meta Pharma envanterindeki hammaddelere öncelik verir. Aktarım sonrası
              mevcut Ar-Ge onay/QC/GMP akışı işler; AI bunları atlamaz.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold block">Müşteri *</span>
              <input
                list="rd-ai-customers"
                value={customers.find(c => c.id === customerId)?.legal_name || ""}
                onChange={e => {
                  const c = customers.find(x => x.legal_name === e.target.value);
                  setCustomerId(c?.id || "");
                }}
                placeholder="Müşteri arayın…"
                className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
                data-testid="rd-ai-customer"
              />
              <datalist id="rd-ai-customers">
                {customers.map(c => <option key={c.id} value={c.legal_name}/>)}
              </datalist>
            </div>
            <Input label="Ürün Adı *" value={productName} onChange={e => setProductName(e.target.value)} data-testid="rd-ai-product-name"/>
          </div>
          <Input label="Ürün Miktarı (ml)" type="number" step="1" value={unitMl} onChange={e => setUnitMl(e.target.value)} data-testid="rd-ai-unit-ml"/>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold block">Ürünü tarif edin</label>
            <textarea
              rows={6}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Örnek: 6 saat kalıcı parlaklık sağlayan, nemlendirici, yapışkan hissi düşük, şeffaf bir lip gloss istiyorum."
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
              data-testid="rd-ai-description"
            />
          </div>
          <Btn
            onClick={generate}
            disabled={busy || !description.trim()}
            className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white"
            data-testid="rd-ai-generate">
            <Sparkles size={14}/> {busy ? "AI hazırlıyor..." : (draft ? "Yeniden Üret" : "Referans Formül Üret")}
          </Btn>
          {draft && (
            <div className="border border-violet-200 rounded p-2 bg-white space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Revizyon Talebi</div>
              <textarea
                rows={2}
                value={revisionInstruction}
                onChange={e => setRevisionInstruction(e.target.value)}
                placeholder="Örn: Bu formülü daha ekonomik yap · Parlaklığı artır · Bu hammaddeyi kullanma..."
                className="w-full px-2 py-1 text-xs border border-slate-300 rounded"
                data-testid="rd-ai-revision-input"
              />
              <Btn onClick={revise} disabled={busy || !revisionInstruction.trim()} variant="secondary" data-testid="rd-ai-revise">
                <RotateCcw size={12}/> Taslağı Revize Et
              </Btn>
            </div>
          )}
          {err && (
            <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded p-2 flex items-start gap-2" data-testid="rd-ai-err">
              <AlertTriangle size={14} className="mt-0.5 shrink-0"/> {err}
            </div>
          )}
        </div>
        <div className="space-y-2">
          {!draft && (
            <div className="border-2 border-dashed border-slate-300 rounded p-8 text-center text-sm text-slate-500" data-testid="rd-ai-empty">
              AI taslağı burada görünecek. Ürünü tarif edip "Referans Formül Üret" butonuna basın.
            </div>
          )}
          {draft && (
            <>
              <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs">
                <b className="text-violet-800">Özet:</b> {draft.product_summary || "—"}
                <div className="mt-1 flex flex-wrap gap-2 text-[10px]">
                  <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 border border-indigo-300">
                    ✓ Envanter: {draft.inventory_material_count}
                  </span>
                  <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">
                    + Yeni öneri: {draft.new_material_count}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded border ${kgValid ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-rose-100 text-rose-800 border-rose-300"}`}>
                    Toplam kg %: {kgTotal.toFixed(2)}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded border ${draft.cost_incomplete ? "bg-rose-100 text-rose-800 border-rose-300" : "bg-slate-100 text-slate-700 border-slate-300"}`}>
                    {draft.cost_incomplete ? "EKSİK MALİYET" : `€${(draft.estimated_cost_per_kg_eur || 0).toFixed(4)}/kg`}
                  </span>
                  <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200">
                    {draft.provider_used}/{draft.model_used}
                  </span>
                </div>
              </div>
              <div className="border border-slate-200 rounded overflow-hidden">
                <table className="w-full text-xs" data-testid="rd-ai-formula-table">
                  <thead className="bg-slate-100 text-slate-600 text-[10px] uppercase">
                    <tr>
                      <th className="text-left px-2 py-1">Hammadde</th>
                      <th className="text-left px-2 py-1">INCI</th>
                      <th className="text-left px-2 py-1">Birim</th>
                      <th className="text-right px-2 py-1">% / Adet</th>
                      <th className="text-left px-2 py-1">Kaynak</th>
                      <th className="text-left px-2 py-1">Fonksiyon</th>
                      <th className="w-6"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {components.map((c, i) => (
                      <tr key={i} className={`border-t border-slate-100 ${c.is_manual ? "bg-amber-50/40" : ""}`} data-testid={`rd-ai-row-${i}`}>
                        <td className="px-2 py-1">
                          <input value={c.material_name} onChange={e => updateLine(i, { material_name: e.target.value })} className="w-full px-1 py-0.5 text-xs border-b border-transparent hover:border-slate-300 bg-transparent"/>
                        </td>
                        <td className="px-2 py-1 text-slate-500">{c.inci || "—"}</td>
                        <td className="px-2 py-1">
                          <select value={c.unit || "kg"} onChange={e => updateLine(i, { unit: e.target.value })} className="text-xs border border-slate-200 rounded">
                            <option value="kg">kg</option><option value="adet">adet</option>
                          </select>
                        </td>
                        <td className="px-2 py-1 text-right">
                          {(c.unit || "kg") === "kg" ? (
                            <input type="number" step="0.01" value={c.percentage || 0} onChange={e => updateLine(i, { percentage: parseFloat(e.target.value) || 0 })} className="w-16 px-1 py-0.5 text-xs border border-slate-200 rounded text-right font-mono"/>
                          ) : (
                            <input type="number" step="1" value={c.qty_adet || 0} onChange={e => updateLine(i, { qty_adet: parseFloat(e.target.value) || 0 })} className="w-16 px-1 py-0.5 text-xs border border-slate-200 rounded text-right font-mono"/>
                          )}
                        </td>
                        <td className="px-2 py-1">
                          {c.is_manual
                            ? <span className="text-[10px] font-bold text-amber-800">+ YENİ ÖNERİ</span>
                            : <span className="text-[10px] font-bold text-indigo-800">
                                ✓ ENVANTERDE
                                {c.stock_status === "LOW_STOCK" && <span className="ml-1 text-rose-700">· Stok az</span>}
                                {c.stock_status === "OUT_OF_STOCK" && <span className="ml-1 text-rose-700">· Stok yok</span>}
                              </span>
                          }
                        </td>
                        <td className="px-2 py-1 text-slate-600 text-[11px]">{c.function || "—"}</td>
                        <td className="px-1 py-1 text-center">
                          <button onClick={() => removeLine(i)} className="text-rose-500 hover:text-rose-700" data-testid={`rd-ai-del-${i}`}><Trash2 size={11}/></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(draft.warnings?.length || draft.development_notes?.length) ? (
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  {draft.warnings?.length > 0 && (
                    <div className="border border-amber-200 bg-amber-50 rounded p-2" data-testid="rd-ai-warnings">
                      <b className="text-amber-900 block mb-1">Uyarılar</b>
                      <ul className="list-disc list-inside space-y-0.5 text-amber-800">{draft.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                    </div>
                  )}
                  {draft.development_notes?.length > 0 && (
                    <div className="border border-indigo-200 bg-indigo-50 rounded p-2" data-testid="rd-ai-notes">
                      <b className="text-indigo-900 block mb-1">Ar-Ge Notları</b>
                      <ul className="list-disc list-inside space-y-0.5 text-indigo-800">{draft.development_notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
                    </div>
                  )}
                </div>
              ) : null}
              <div className="text-[10px] text-slate-500 italic border-l-2 border-violet-300 pl-2">
                AI tarafından oluşturulan referans Ar-Ge taslağıdır. Üretim öncesinde yetkili Ar-Ge personeli tarafından değerlendirilmesi ve onaylanması gerekir.
              </div>
            </>
          )}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-3 mt-3 border-t border-slate-200">
        <Btn variant="secondary" onClick={onClose} data-testid="rd-ai-cancel">Vazgeç</Btn>
        <Btn
          onClick={() => setConfirmOpen(true)}
          disabled={!draft || !kgValid || !customerId || !productName.trim() || transferBusy}
          className="bg-gradient-to-r from-emerald-600 to-teal-500 text-white"
          data-testid="rd-ai-transfer">
          <ArrowRightCircle size={14}/> Ar-Ge Projesine Aktar
        </Btn>
      </div>
      {confirmOpen && (
        <Modal open={true} onClose={() => setConfirmOpen(false)} title="Aktarımı Onayla" size="md">
          <div className="space-y-3 text-sm">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs">
              Bu AI taslağı mevcut Ar-Ge Projesi yapısına aktarılacaktır. Aktarım sonrasında normal Ar-Ge iş akışı üzerinden düzenlenebilir ve onaylanabilir.
            </div>
            {draft?.new_material_count > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded p-2 text-xs text-amber-800">
                {draft.new_material_count} adet yeni hammadde önerisi <b>ENVANTER KAYDI GEREKLİ</b> olarak işaretlenecek. Envantere otomatik eklenmeyecek.
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setConfirmOpen(false)} data-testid="rd-ai-transfer-cancel">İptal</Btn>
              <Btn onClick={doTransfer} disabled={transferBusy} className="bg-emerald-600 text-white" data-testid="rd-ai-transfer-confirm">
                {transferBusy ? "Aktarılıyor..." : "Ar-Ge Projesine Aktar"}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  );
}

// ============================================================================
// AI Product Content Assistant (iter162)
// ============================================================================
const SECTIONS = [
  { key: "description", label: "Ürün Tanıtım Metni" },
  { key: "highlights",  label: "Öne Çıkan İçerikler" },
  { key: "marketing",   label: "Satış & Pazarlama Önerileri" },
  { key: "social",      label: "Sosyal Medya İçeriği" },
  { key: "claims",      label: "Claim Önerileri" },
];
const CLAIM_COLOR = {
  GREEN:  "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30",
  YELLOW: "bg-amber-100 text-amber-800 dark:bg-amber-900/30",
  ORANGE: "bg-orange-100 text-orange-800 dark:bg-orange-900/30",
  RED:    "bg-red-100 text-red-800 dark:bg-red-900/30",
  GREY:   "bg-slate-100 text-slate-700 dark:bg-slate-800",
};

function AIContentAssistant({ sample, onClose }) {
  const [selected, setSelected] = useState({});
  const [userPrompt, setUserPrompt] = useState("");
  const [aiModel, setAiModel] = useState("claude-sonnet-5");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (k) => setSelected(s => ({ ...s, [k]: !s[k] }));
  const generate = async () => {
    setBusy(true);
    try {
      const sections = Object.keys(selected).filter(k => selected[k]);
      const res = await api.post("/rd/ai-content/generate", {
        rd_sample_id: sample.id,
        sections, user_prompt: userPrompt, ai_model: aiModel, use_cache: true,
      });
      setResult(res);
    } catch (e) {
      alert(e?.response?.data?.detail || "Oluşturma hatası");
    } finally { setBusy(false); }
  };
  const dlPdf = async () => {
    if (!result) return;
    await api.downloadFile(`/rd/ai-content/${result.id}/pdf`, `urun_bilgi_${result.doc_no}.pdf`);
  };
  const approve = async () => {
    if (!result) return;
    if (!window.confirm("Bu içeriği onaylayıp immutable yap?")) return;
    await api.post(`/rd/ai-content/${result.id}/approve`);
    setResult({ ...result, status: "APPROVED", immutable: true });
  };

  if (!result) {
    return (
      <div className="space-y-3 text-sm">
        <div className="p-3 rounded bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 text-xs">
          <div className="flex items-start gap-2">
            <Sparkles size={14} className="mt-0.5 shrink-0"/>
            <div><b>AI Hallucination Firewall:</b> AI ingredient/INCI/CAS/oran/test/klinik veri üretemez. Etiket deterministik olarak formülden üretilir (AI çağrılmaz). Sadece seçilen bölümler tek optimize edilmiş çağrıda üretilir — kredi tüketimini minimize eder.</div>
          </div>
        </div>
        <div>
          <b>Oluşturulacak Bölümler (opsiyonel — sadece istenenler AI ile üretilir):</b>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {SECTIONS.map(s => (
              <label key={s.key} className="flex items-center gap-2 p-2 rounded border border-slate-200 dark:border-slate-700 cursor-pointer">
                <input type="checkbox" checked={!!selected[s.key]} onChange={() => toggle(s.key)} data-testid={`ai-section-${s.key}`}/>
                {s.label}
              </label>
            ))}
          </div>
          <div className="text-[10px] text-slate-500 mt-2">
            Not: Etiket / Mevzuat Bilgileri (Türkiye Kozmetik Yönetmeliği) daima deterministik olarak üretilir — AI çağrılmaz.
          </div>
        </div>
        <Textarea label="Ürünü Kısaca Tanıt (opsiyonel — yardımcı bağlam, formula fact değil)"
          placeholder="Örn: Yağlı ve akneye eğilimli ciltler için geliştirilen, hafif yapılı günlük bakım serumu."
          value={userPrompt} onChange={e => setUserPrompt(e.target.value)}
          data-testid="ai-user-prompt"/>
        <Select label="AI Modeli" value={aiModel} onChange={e => setAiModel(e.target.value)}>
          <option value="claude-sonnet-5">Claude Sonnet 5</option>
          <option value="gpt-5.6-sol">GPT 5.6 Sol</option>
          <option value="gemini-3.1-pro-preview">Gemini 3.1 Pro</option>
        </Select>
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
          <Btn onClick={generate} disabled={busy} data-testid="ai-generate-btn">
            {busy ? "Oluşturuluyor..." : "Seçilen İçerikleri Oluştur"}
          </Btn>
        </div>
      </div>
    );
  }

  const label = result.label || {};
  const ai = result.ai_result?.sections || {};
  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-center justify-between p-2 rounded bg-slate-100 dark:bg-slate-800">
        <div className="text-xs">
          <b>Doküman:</b> {result.doc_no} · <b>Rev:</b> {result.revision} · <b>Durum:</b> {result.status}
          {result.cache_hit && <span className="ml-2 px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 text-[10px]">CACHE HIT</span>}
        </div>
        <div className="flex gap-2">
          <Btn size="sm" onClick={dlPdf} data-testid="ai-download-pdf" title="AI Sonuç PDF İndir"><FileText size={12}/></Btn>
          {result.status !== "APPROVED" && <Btn size="sm" onClick={approve} data-testid="ai-approve"><CheckCircle size={12}/> Onayla</Btn>}
          <Btn size="sm" variant="secondary" onClick={() => setResult(null)}>Yeniden</Btn>
        </div>
      </div>
      {/* Etiket */}
      <details open className="p-2 border rounded border-slate-200 dark:border-slate-700">
        <summary className="cursor-pointer text-sm font-semibold">1. Etiket / Mevzuat Bilgileri (Türkiye) — Deterministik</summary>
        <div className="mt-2 text-xs space-y-1">
          <div><b>Ürün:</b> {label.product_name} · <b>Fonksiyon:</b> {label.product_function}</div>
          <div><b>Sorumlu Firma:</b> {label.responsible_person} · <b>Üretici:</b> {label.manufacturer}</div>
          <div><b>Saklama:</b> {label.storage}</div>
          <div><b>SKT/PAO:</b> {label.expiry_or_pao}</div>
          <div><b>Uyarılar:</b>
            <ul className="list-disc list-inside ml-2">{(label.warnings_mandatory || []).map((w, i) => <li key={i}>{w}</li>)}</ul>
          </div>
          <div><b>İçerikler (INCI, kozmetik sıralı):</b>
            <div className="mt-1 font-mono text-[10px] p-2 bg-slate-50 dark:bg-slate-800 rounded">{(label.ingredients_inci || []).join(", ")}</div>
          </div>
          {label.unverified_ingredients_count > 0 && (
            <div className="p-2 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <b>UYARI:</b> {label.unverified_ingredients_count} bileşen INCI bilgisi eksik: {(label.unverified_ingredients || []).join(", ")}. UZMAN KONTROLÜ GEREKLİ.
            </div>
          )}
          <div className="text-[10px] text-slate-500">{label.regulatory_source_note}</div>
        </div>
      </details>
      {/* AI sections */}
      {ai.description && (
        <details open className="p-2 border rounded border-violet-200 dark:border-violet-800">
          <summary className="cursor-pointer text-sm font-semibold">2. Ürün Tanıtımı <span className="text-[10px] text-violet-600 dark:text-violet-300">AI ÖNERİSİ</span></summary>
          <div className="mt-2 text-xs whitespace-pre-line">{ai.description}</div>
        </details>
      )}
      {ai.highlights && (
        <details open className="p-2 border rounded border-violet-200 dark:border-violet-800">
          <summary className="cursor-pointer text-sm font-semibold">3. Öne Çıkan İçerikler <span className="text-[10px] text-violet-600 dark:text-violet-300">AI ÖNERİSİ</span></summary>
          <ul className="mt-2 text-xs space-y-1">
            {ai.highlights.map((h, i) => <li key={i}><b>{h.inci}</b> → {h.benefit_tr}</li>)}
          </ul>
          {result.ai_result?.rejected_highlights?.length > 0 && (
            <div className="mt-1 text-[10px] text-amber-700">Firewall reddi: {result.ai_result.rejected_highlights.join(", ")} (formula'da yok)</div>
          )}
        </details>
      )}
      {ai.marketing && (
        <details open className="p-2 border rounded border-violet-200 dark:border-violet-800">
          <summary className="cursor-pointer text-sm font-semibold">4. Pazarlama Önerileri <span className="text-[10px] text-violet-600 dark:text-violet-300">AI ÖNERİSİ</span></summary>
          <div className="mt-2 text-xs space-y-1">
            <div><b>Hedef Kitle:</b> {ai.marketing.target_audience}</div>
            <div><b>Konumlandırma:</b> {ai.marketing.positioning}</div>
            <div><b>Öne Çıkarılabilecek Özellikler:</b><ul className="list-disc list-inside ml-2">{(ai.marketing.strong_points || []).map((p, i) => <li key={i}>{p}</li>)}</ul></div>
            <div><b>Satış Argümanları:</b><ul className="list-disc list-inside ml-2">{(ai.marketing.selling_arguments || []).map((p, i) => <li key={i}>{p}</li>)}</ul></div>
          </div>
        </details>
      )}
      {ai.social && (
        <details open className="p-2 border rounded border-violet-200 dark:border-violet-800">
          <summary className="cursor-pointer text-sm font-semibold">5. Sosyal Medya <span className="text-[10px] text-violet-600 dark:text-violet-300">AI ÖNERİSİ</span></summary>
          <div className="mt-2 text-xs space-y-1">
            <div>{ai.social.post_tr}</div>
            <div className="italic text-slate-500">{ai.social.short_pitch}</div>
            <div className="font-mono text-[10px] text-violet-700">{(ai.social.hashtags || []).join(" ")}</div>
          </div>
        </details>
      )}
      {ai.claims && (
        <details open className="p-2 border rounded border-violet-200 dark:border-violet-800">
          <summary className="cursor-pointer text-sm font-semibold">6. Claim Değerlendirmesi <span className="text-[10px] text-violet-600 dark:text-violet-300">DETERMINİSTİK SCREENER</span></summary>
          <ul className="mt-2 text-xs space-y-1">
            {ai.claims.map((c, i) => {
              const lvl = c.screen?.level || "GREY";
              return (
                <li key={i} className={`p-2 rounded ${CLAIM_COLOR[lvl]}`}>
                  <span className="font-mono text-[10px] mr-2">{lvl}</span>{c.claim_tr}
                  {c.screen?.reason && <div className="text-[10px] mt-0.5 opacity-75">{c.screen.reason}</div>}
                </li>
              );
            })}
          </ul>
        </details>
      )}
      {!result.ai_result?.ok && Object.keys(ai).length === 0 && result.sections_requested?.length > 0 && (
        <div className="p-2 rounded bg-amber-50 dark:bg-amber-900/20 text-xs text-amber-800 dark:text-amber-200">
          AI çağrısı başarısız: {result.ai_result?.error || "bilinmiyor"}
        </div>
      )}
    </div>
  );
}
